import React, { useCallback } from "react"
import styles from "./AlertDialog.module.scss"
import { Button, Modal, ModalProps } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { formTranslation } from "../../locales/form"
import { ButtonVariant } from "react-bootstrap/types"
import { dialogsTestIds } from "../../utility/tests/testId/dialogs"
import cn from "classnames"

export interface AlertDialogProps extends ModalProps {
    show: boolean
    title: string
    message?: string
    onClose: () => void
    onSubmit: () => void
    onExited?: (node: HTMLElement) => void
    variant?: ButtonVariant
    submitBtnText?: string
    cancelBtnText?: string
    cancelable?: boolean
    modalClassName?: string
    waitForClosing?: boolean // need to prevent deleting component before onClose is finished
}

const AlertDialog: React.FC<AlertDialogProps> = props => {
    const { t } = useTranslation()
    const {
        show,
        title,
        message,
        onClose,
        onSubmit,
        onExited,
        submitBtnText,
        cancelBtnText,
        variant = "primary",
        cancelable = true,
        modalClassName,
        waitForClosing,
        ...modalProps
    } = props

    const handleSubmit = useCallback(() => {
        onClose()
        if (waitForClosing) {
            queueMicrotask(() => onSubmit())
            return
        }
        onSubmit()
    }, [onSubmit, onClose])

    return (
        <Modal
            {...modalProps}
            dialogClassName={cn(styles.alertDialog, modalClassName)}
            contentClassName={styles.modalContent}
            show={show}
            onHide={onClose}
            onExited={onExited}
        >
            <Modal.Body className={styles.modalBody}>
                <Modal.Title className={styles.modalTitle}>{title}</Modal.Title>
                {message && <p className={styles.modalText}>{message}</p>}
            </Modal.Body>

            <Modal.Footer className={styles.modalFooter}>
                {cancelable && (
                    <Button variant="light" onClick={onClose} data-testid={dialogsTestIds.alertDialogCancel}>
                        {cancelBtnText || t(formTranslation.cancel)}
                    </Button>
                )}
                <Button variant={variant} onClick={handleSubmit} data-testid={dialogsTestIds.alertDialogSubmit}>
                    {submitBtnText || t(formTranslation.continue)}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

export default AlertDialog
